/**
 * Single source of truth for every per-type BrickLink fact.
 *
 * This file holds DATA ONLY and imports nothing. Every URL is built in
 * `@utils/itemImageUrl`, which is the only module in ui/src permitted to
 * contain a bricklink.com literal. Keeping the data here and the strings there
 * avoids an import cycle and gives the image-funnel lint rule exactly one
 * allow-list entry.
 *
 * Column meanings:
 *   letter        BrickLink catalog code, used in catalogitem.page URLs.
 *                 null when the type is not a catalog top-level type.
 *   imageCode     BrickLink image path segment. null when BrickLink hosts no
 *                 image for the type.
 *   storeCode     BrickLink mass-upload XML code. Diverges from `letter` on
 *                 purpose: the upload schema treats GEAR and STICKER_SHEET as
 *                 parts. Merging the two would silently corrupt store uploads,
 *                 so the guard test asserts the divergence.
 *   wantedCode    BrickLink wanted-list XML code. A third, separate divergence.
 *                 The wanted list schema DOES accept 'G' for gear, so this
 *                 column follows `letter` there and not `storeCode`. A sticker
 *                 sheet is catalogued as a part, so it takes 'P' where `letter`
 *                 holds null.
 *   colorless     true when BrickLink serves the image under colour 0 only.
 *                 Keyed on the BrickBuddy type, never on imageCode: PART and
 *                 STICKER_SHEET share 'PN' but differ here, and
 *                 ItemImage/PN/0/3001.png returns 404 while
 *                 ItemImage/PN/0/8643stk01.png returns 200.
 *   storedSuffix  Local suffix BrickBuddy appends to items.number so the row
 *                 does not collide with the parent SET on the unique index.
 *                 BrickLink knows the item by the bare number, so the suffix
 *                 must be stripped before any URL is built.
 */
export const ITEM_TYPE_MODEL = Object.freeze({
    PART:          Object.freeze({ letter: 'P',  imageCode: 'PN', storeCode: 'P',  wantedCode: 'P',  colorless: false, storedSuffix: null }),
    GEAR:          Object.freeze({ letter: 'G',  imageCode: 'GN', storeCode: 'P',  wantedCode: 'G',  colorless: false, storedSuffix: null }),
    MINIFIG:       Object.freeze({ letter: 'M',  imageCode: 'MN', storeCode: 'M',  wantedCode: 'M',  colorless: true,  storedSuffix: null }),
    SET:           Object.freeze({ letter: 'S',  imageCode: 'SN', storeCode: 'S',  wantedCode: 'S',  colorless: true,  storedSuffix: null }),
    BOOK:          Object.freeze({ letter: 'B',  imageCode: 'BN', storeCode: 'B',  wantedCode: 'B',  colorless: true,  storedSuffix: null }),
    INSTRUCTION:   Object.freeze({ letter: 'I',  imageCode: 'IN', storeCode: 'I',  wantedCode: 'I',  colorless: true,  storedSuffix: '-instruction' }),
    ORIGINAL_BOX:  Object.freeze({ letter: 'O',  imageCode: 'ON', storeCode: 'O',  wantedCode: 'O',  colorless: true,  storedSuffix: '-original_box' }),
    CATALOG:       Object.freeze({ letter: 'C',  imageCode: 'CN', storeCode: 'C',  wantedCode: 'C',  colorless: true,  storedSuffix: null }),
    STICKER_SHEET: Object.freeze({ letter: null, imageCode: 'PN', storeCode: 'P',  wantedCode: 'P',  colorless: true,  storedSuffix: null }),
    UNSORTED_LOT:  Object.freeze({ letter: null, imageCode: null, storeCode: null, wantedCode: null, colorless: true,  storedSuffix: null })
});

// Canonical list — mirrors the item_types table ENUM in the database.
export const ITEM_TYPES = Object.keys(ITEM_TYPE_MODEL);

const buildCodeMap = (column) => Object.freeze(
    Object.fromEntries(
        Object.entries(ITEM_TYPE_MODEL)
            .filter(([, model]) => model[column] !== null)
            .map(([type, model]) => [type, model[column]])
    )
);

// BrickBuddy type -> BrickLink single-letter catalog code.
export const BRICKLINK_TYPE_CODE_MAP = buildCodeMap('letter');

// BrickBuddy type -> BrickLink mass-upload XML code. See `storeCode` above.
export const BRICKLINK_STORE_INVENTORY_TYPE_CODE_MAP = buildCodeMap('storeCode');

// BrickBuddy type -> BrickLink wanted-list XML code. See `wantedCode` above.
export const BRICKLINK_WANTED_LIST_TYPE_CODE_MAP = buildCodeMap('wantedCode');

// BrickBuddy type -> BrickLink image path segment.
export const BRICKLINK_IMAGE_TYPE_MAP = buildCodeMap('imageCode');

/**
 * Resolves a type from any source to a key of ITEM_TYPE_MODEL.
 *
 * Case folding is load-bearing, not cosmetic: prod holds items rows with
 * type 'part' in lower case, and one of them has a lot, so it reaches the
 * inventory, batch and stow tables.
 *
 * @param {string} itemType - The item type from any source
 * @returns {string|null} A key of ITEM_TYPE_MODEL, or the upper-cased input
 *                        when it maps to no known type
 */
export const normalizeItemType = (itemType) => {
    if (!itemType) return null;

    const upperType = String(itemType).toUpperCase();

    if (ITEM_TYPES.includes(upperType)) {
        return upperType;
    }

    // Handle Brickognize / common variations
    const ALIASES = {
        'part': 'PART', 'parts': 'PART',
        'fig': 'MINIFIG', 'figure': 'MINIFIG', 'minifigure': 'MINIFIG',
        'set': 'SET', 'sets': 'SET'
    };
    return ALIASES[String(itemType).toLowerCase()] || upperType;
};

/**
 * Resolves a type or a BrickLink single-letter code to a key of
 * ITEM_TYPE_MODEL. Returns null when the input maps to no known type — the
 * callers run inside table cell renderers, where a throw unmounts the page.
 *
 * @param {string} itemType - A BrickBuddy type name or BrickLink letter code
 * @returns {string|null} A key of ITEM_TYPE_MODEL, or null
 */
export const resolveItemTypeKey = (itemType) => {
    if (!itemType) return null;

    const upperType = String(itemType).toUpperCase();
    if (ITEM_TYPE_MODEL[upperType]) return upperType;

    const byLetter = Object.keys(ITEM_TYPE_MODEL)
        .find(type => ITEM_TYPE_MODEL[type].letter === upperType);

    return byLetter || null;
};
